/**
 * DPRSettingsManpowerTab
 *
 * Manpower Set-Up for a project:
 *   - Add every crew member with their name, position (role), shift, and — for welders — allocated station.
 *   - Footprint View: a visual grid of the production line showing all stations and who is assigned to each.
 *
 * Reads the production line setup (stations by type) from the API so that:
 *   - the station picker only shows welding station numbers for welders.
 *   - the footprint view groups cards by station type.
 */

import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { get } from 'lodash';
import {
  Users,
  Plus,
  Pencil,
  Trash2,
  X,
  ChevronDown,
  ChevronUp,
  LayoutGrid,
  List,
  Wrench,
  Paintbrush2,
  ScanLine,
  Sun,
  Moon,
  Clock,
} from 'lucide-react';
import { Button, Spinner, ConfirmationModal } from '@crc-evans/ui-components';
import { message } from 'antd';
import axiosConfig from '../../services/axiosConfig';
import { fetchRoles } from '../../redux/roles/action';
import { fetchSowDetails } from '../../redux/sowDetails/actions';

// ─── Constants ──────────────────────────────────────────────────────────────

const SHIFTS = [
  { value: 'Day', label: 'Day', icon: Sun, color: '#D97706', bg: '#FFFBEB' },
  { value: 'Night', label: 'Night', icon: Moon, color: '#7C3AED', bg: '#F5F3FF' },
  { value: 'Both', label: 'Both', icon: Clock, color: '#0891B2', bg: '#ECFEFF' },
];

const STATION_TYPE_META = {
  welding: { label: 'Welding', icon: Wrench, color: '#D97706', bg: '#FFFBEB', border: '#FDE68A' },
  coating: { label: 'Coating', icon: Paintbrush2, color: '#7C3AED', bg: '#F5F3FF', border: '#DDD6FE' },
  ndt: { label: 'NDT', icon: ScanLine, color: '#0891B2', bg: '#ECFEFF', border: '#A5F3FC' },
};

const isWelderPosition = (position) =>
  String(position || '').trim().toLowerCase().includes('weld');

// ─── Small reusable components ───────────────────────────────────────────────

const ShiftBadge = ({ shift }) => {
  const meta = SHIFTS.find((s) => s.value === shift) ?? SHIFTS[0];
  const Icon = meta.icon;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '3px 10px',
        background: meta.bg,
        color: meta.color,
        borderRadius: '20px',
        fontSize: '12px',
        fontWeight: 600,
      }}
    >
      <Icon size={12} />
      {meta.label}
    </span>
  );
};

const SimpleSelect = ({ value, options, onChange, placeholder, disabled }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const selected = options.find((o) => o.value === value);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} style={{ position: 'relative', width: '100%' }}>
      <div
        onClick={() => !disabled && setOpen((o) => !o)}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '9px 12px',
          borderRadius: '8px',
          border: open ? '2px solid #3B82F6' : '1px solid #E2E8F0',
          background: disabled ? '#F8FAFC' : '#FFF',
          cursor: disabled ? 'not-allowed' : 'pointer',
          fontSize: '14px',
          color: selected ? '#1F2937' : '#9CA3AF',
          minHeight: '40px',
        }}
      >
        <span>{selected ? selected.label : placeholder}</span>
        {open ? <ChevronUp size={16} color="#6B7280" /> : <ChevronDown size={16} color="#6B7280" />}
      </div>
      {open && !disabled && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            zIndex: 9999,
            background: '#FFF',
            border: '1px solid #E5E7EB',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            maxHeight: '220px',
            overflowY: 'auto',
          }}
        >
          {options.map((opt) => (
            <div
              key={opt.value}
              onClick={() => { onChange(opt.value); setOpen(false); }}
              style={{
                padding: '10px 14px',
                fontSize: '14px',
                cursor: 'pointer',
                background: opt.value === value ? '#EFF6FF' : 'transparent',
                color: opt.value === value ? '#1D4ED8' : '#374151',
              }}
              onMouseEnter={(e) => { if (opt.value !== value) e.currentTarget.style.background = '#F8FAFC'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = opt.value === value ? '#EFF6FF' : 'transparent'; }}
            >
              {opt.label}
            </div>
          ))}
          {options.length === 0 && (
            <div style={{ padding: '10px 14px', color: '#94A3B8', fontSize: '13px' }}>No options</div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Footprint View ──────────────────────────────────────────────────────────

const FootprintView = ({ sowData, crew }) => {
  const stationTypes = ['welding', 'coating', 'ndt'];

  // Aggregate stations from all pipelines
  const aggregated = (() => {
    const pipelines = Array.isArray(sowData) ? sowData : [];
    const weldingSet = new Set();
    const coatingSet = new Set();
    const ndtMap = {};
    pipelines.forEach((pl) => {
      (pl.weldingStations ?? []).forEach((n) => weldingSet.add(n));
      (pl.coatingStations ?? []).forEach((n) => coatingSet.add(n));
      (pl.ndtStations ?? []).forEach((entry) => {
        const n = typeof entry === 'object' ? entry.stationNumber : entry;
        const ndtType = typeof entry === 'object' ? entry.ndtType : '';
        if (n != null) ndtMap[n] = ndtType || '';
      });
    });
    return {
      weldingStations: [...weldingSet],
      coatingStations: [...coatingSet],
      ndtStations: Object.keys(ndtMap).map(Number),
      ndtTypeMap: ndtMap,
    };
  })();

  const crewByStation = {};
  crew.forEach((m) => {
    if (m.station != null) {
      if (!crewByStation[m.station]) crewByStation[m.station] = [];
      crewByStation[m.station].push(m);
    }
  });

  const unassigned = crew.filter((m) => m.station == null);

  const hasAnyStation =
    aggregated.weldingStations.length > 0 ||
    aggregated.coatingStations.length > 0 ||
    aggregated.ndtStations.length > 0;

  if (!hasAnyStation) {
    return (
      <div style={{ padding: '32px', textAlign: 'center', color: '#94A3B8', fontSize: '14px' }}>
        No production line configured. Set up the Production Line in the Pipelines tab first.
      </div>
    );
  }

  return (
    <div>
      {stationTypes.map((type) => {
        const stations =
          type === 'ndt'
            ? [...aggregated.ndtStations].sort((a, b) => a - b)
            : [...(aggregated[`${type}Stations`] ?? [])].sort((a, b) => a - b);
        if (stations.length === 0) return null;
        const meta = STATION_TYPE_META[type];
        const Icon = meta.icon;
        return (
          <div key={type} style={{ marginBottom: '28px' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '12px',
              }}
            >
              <div
                style={{
                  background: meta.bg,
                  color: meta.color,
                  padding: '6px',
                  borderRadius: '6px',
                  display: 'flex',
                }}
              >
                <Icon size={16} />
              </div>
              <span style={{ fontWeight: 600, fontSize: '14px', color: '#1E293B' }}>
                {meta.label} Stations
              </span>
            </div>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '12px',
              }}
            >
              {stations.map((stNum) => {
                const assigned = crewByStation[stNum] ?? [];
                const ndtType = type === 'ndt' ? aggregated.ndtTypeMap[stNum] : null;
                return (
                  <div
                    key={stNum}
                    style={{
                      background: '#FFFFFF',
                      border: `1.5px solid ${meta.border}`,
                      borderRadius: '10px',
                      padding: '14px 16px',
                      minWidth: '160px',
                      maxWidth: '200px',
                      flexShrink: 0,
                    }}
                  >
                    <div
                      style={{
                        fontSize: '11px',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        color: meta.color,
                        letterSpacing: '0.06em',
                        marginBottom: '4px',
                      }}
                    >
                      Station {stNum}
                    </div>
                    {ndtType && (
                      <div style={{ fontSize: '10px', color: '#1D4ED8', fontWeight: 600, marginBottom: '8px' }}>
                        {ndtType}
                      </div>
                    )}
                    {assigned.length === 0 ? (
                      <div
                        style={{
                          fontSize: '12px',
                          color: '#CBD5E1',
                          fontStyle: 'italic',
                        }}
                      >
                        Unassigned
                      </div>
                    ) : (
                      assigned.map((m) => (
                        <div
                          key={m.id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            marginBottom: '6px',
                          }}
                        >
                          <div
                            style={{
                              width: '28px',
                              height: '28px',
                              borderRadius: '50%',
                              background: '#E0F2FE',
                              color: '#0369A1',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '11px',
                              fontWeight: 700,
                              flexShrink: 0,
                            }}
                          >
                            {(m.name || '?').charAt(0).toUpperCase()}
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <div
                              style={{
                                fontSize: '12px',
                                fontWeight: 600,
                                color: '#1E293B',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {m.name}
                            </div>
                            <ShiftBadge shift={m.shift} />
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Unassigned crew */}
      {unassigned.length > 0 && (
        <div style={{ marginTop: '8px' }}>
          <div
            style={{
              fontWeight: 600,
              fontSize: '13px',
              color: '#94A3B8',
              marginBottom: '10px',
            }}
          >
            Not station-assigned
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {unassigned.map((m) => (
              <div
                key={m.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 14px',
                  background: '#F8FAFC',
                  border: '1px solid #E2E8F0',
                  borderRadius: '8px',
                }}
              >
                <div
                  style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: '50%',
                    background: '#F1F5F9',
                    color: '#475569',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '11px',
                    fontWeight: 700,
                    flexShrink: 0,
                  }}
                >
                  {(m.name || '?').charAt(0).toUpperCase()}
                </div>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#1E293B' }}>{m.name}</div>
                  <div style={{ fontSize: '12px', color: '#64748B' }}>{m.position}</div>
                </div>
                <ShiftBadge shift={m.shift} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const DPRSettingsManpowerTab = ({ selectedProject, roles, sowData, onFetchRoles, onFetchSowDetails }) => {
  const [crew, setCrew] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'footprint'

  // Modal
  const [showModal, setShowModal] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);

  // Form fields
  const [formName, setFormName] = useState('');
  const [formPosition, setFormPosition] = useState('');
  const [formShift, setFormShift] = useState('');
  const [formStation, setFormStation] = useState('');

  const projectId = selectedProject?.id;

  // Build station options from all pipelines' production line rows
  const allStationOptions = (() => {
    const pipelines = Array.isArray(sowData) ? sowData : [];
    const opts = [];
    const seen = new Set();
    pipelines.forEach((pl) => {
      (pl.weldingStations ?? []).forEach((n) => {
        const key = `w-${n}`;
        if (!seen.has(key)) {
          seen.add(key);
          opts.push({ value: String(n), label: `St. ${n} — Welding`, type: 'welding' });
        }
      });
      (pl.coatingStations ?? []).forEach((n) => {
        const key = `c-${n}`;
        if (!seen.has(key)) {
          seen.add(key);
          opts.push({ value: String(n), label: `St. ${n} — Coating`, type: 'coating' });
        }
      });
      (pl.ndtStations ?? []).forEach((entry) => {
        const n = typeof entry === 'object' ? entry.stationNumber : entry;
        const ndtType = typeof entry === 'object' ? entry.ndtType : '';
        const key = `ndt-${n}`;
        if (!seen.has(key)) {
          seen.add(key);
          opts.push({
            value: String(n),
            label: ndtType ? `St. ${n} — NDT (${ndtType})` : `St. ${n} — NDT`,
            type: 'ndt',
          });
        }
      });
    });
    return opts.sort((a, b) => Number(a.value) - Number(b.value));
  })();

  // Legacy: welding-only options for welder station assignment
  const weldingStationOptions = allStationOptions.filter((o) => o.type === 'welding');

  const roleOptions = (roles || []).map((r) => ({
    value: r.name ?? r.roleName ?? '',
    label: r.name ?? r.roleName ?? '',
  }));

  const shiftOptions = SHIFTS.map((s) => ({ value: s.value, label: s.label }));

  // Fetch SOW (pipelines) for station options + crew list
  useEffect(() => {
    if (!projectId) return;
    onFetchSowDetails?.(projectId);
    setIsLoading(true);
    axiosConfig
      .get(`/manpower-setup?projectId=${projectId}`)
      .then((res) => {
        const d = res.data?.data ?? res.data;
        setCrew(Array.isArray(d) ? d : []);
      })
      .catch(() => setCrew([]))
      .finally(() => setIsLoading(false));
  }, [projectId]);

  useEffect(() => {
    onFetchRoles?.();
  }, [onFetchRoles]);

  const resetForm = () => {
    setFormName('');
    setFormPosition('');
    setFormShift('');
    setFormStation('');
    setEditRecord(null);
  };

  const openAdd = () => {
    resetForm();
    setShowModal(true);
  };

  const openEdit = (record) => {
    setEditRecord(record);
    setFormName(record.name ?? '');
    setFormPosition(record.position ?? '');
    setFormShift(record.shift ?? '');
    setFormStation(record.station != null ? String(record.station) : '');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  const handleSave = async () => {
    const name = formName.trim();
    const position = formPosition.trim();
    if (!name || !position || !formShift) {
      message.warning('Name, Position, and Shift are required.');
      return;
    }
    const station =
      isWelderPosition(position) && formStation !== ''
        ? Number(formStation)
        : null;

    if (editRecord) {
      const updated = crew.map((m) =>
        m.id === editRecord.id
          ? { ...m, name, position, shift: formShift, station }
          : m
      );
      await persistCrew(updated);
    } else {
      const newMember = {
        id: `local-${Date.now()}`,
        name,
        position,
        shift: formShift,
        station,
      };
      await persistCrew([...crew, newMember]);
    }
    closeModal();
  };

  const persistCrew = async (crewList) => {
    if (!projectId) return;
    setIsSaving(true);
    try {
      const payload = {
        projectId: Number(projectId),
        crew: crewList.map(({ id, name, position, shift, station }) => ({
          id: String(id).startsWith('local-') ? undefined : id,
          name,
          position,
          shift,
          station: station ?? undefined,
        })),
      };
      const res = await axiosConfig.put('/manpower-setup', payload);
      const saved = res.data?.data ?? res.data;
      if (Array.isArray(saved)) {
        setCrew(saved);
      } else {
        setCrew(crewList);
      }
      message.success('Manpower setup saved.');
    } catch (err) {
      console.error('Error saving manpower setup:', err);
      message.error('Failed to save manpower setup.');
    } finally {
      setIsSaving(false);
    }
  };

  const confirmDelete = (record) => {
    setRecordToDelete(record);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!recordToDelete) return;
    const updated = crew.filter((m) => m.id !== recordToDelete.id);
    setIsDeleteModalOpen(false);
    setRecordToDelete(null);
    await persistCrew(updated);
  };

  if (!selectedProject) {
    return (
      <div style={{ padding: '24px', color: '#64748B' }}>
        Select a project to configure manpower.
      </div>
    );
  }

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
        <Spinner size="medium" />
      </div>
    );
  }

  return (
    <div>
      {/* Card */}
      <div
        style={{
          background: '#FFFFFF',
          borderRadius: '12px',
          padding: '24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            flexWrap: 'wrap',
            gap: '16px',
            marginBottom: '24px',
          }}
        >
          <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
            <div
              style={{
                background: '#EFF6FF',
                color: '#3B82F6',
                padding: '10px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Users size={24} />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#0F172A' }}>
                Manpower Set-Up
              </h3>
              <p style={{ margin: '4px 0 0 0', color: '#64748B', fontSize: '14px' }}>
                Assign crew members to positions, shifts, and stations before project start.
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            {/* View toggle */}
            <div
              style={{
                display: 'flex',
                background: '#F1F5F9',
                borderRadius: '8px',
                padding: '3px',
                gap: '2px',
              }}
            >
              {[
                { mode: 'list', Icon: List, label: 'List' },
                { mode: 'footprint', Icon: LayoutGrid, label: 'Footprint' },
              ].map(({ mode, Icon, label }) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => setViewMode(mode)}
                  title={label}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                    padding: '6px 12px',
                    borderRadius: '6px',
                    border: 'none',
                    cursor: 'pointer',
                    background: viewMode === mode ? '#FFFFFF' : 'transparent',
                    color: viewMode === mode ? '#1D4ED8' : '#64748B',
                    fontWeight: viewMode === mode ? 600 : 400,
                    fontSize: '13px',
                    boxShadow: viewMode === mode ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                    transition: 'all 0.15s',
                  }}
                >
                  <Icon size={15} />
                  {label}
                </button>
              ))}
            </div>
            <Button
              label="Add Crew Member"
              variant="primary"
              size="small"
              icon={Plus}
              onClick={openAdd}
              disabled={isSaving}
            />
          </div>
        </div>

        {/* ── LIST VIEW ── */}
        {viewMode === 'list' && (
          <>
            {crew.length === 0 ? (
              <div
                style={{
                  textAlign: 'center',
                  padding: '48px 24px',
                  color: '#94A3B8',
                  fontSize: '14px',
                  border: '2px dashed #E2E8F0',
                  borderRadius: '10px',
                }}
              >
                No crew members yet. Click &quot;Add Crew Member&quot; to get started.
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                {/* Table header */}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 120px 120px 80px',
                    gap: '12px',
                    padding: '10px 12px',
                    borderBottom: '1px solid #E2E8F0',
                    fontSize: '11px',
                    fontWeight: 700,
                    color: '#64748B',
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                  }}
                >
                  <div>Name</div>
                  <div>Position</div>
                  <div>Shift</div>
                  <div>Station</div>
                  <div style={{ textAlign: 'center' }}>Actions</div>
                </div>
                {crew.map((m) => (
                  <div
                    key={m.id}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr 120px 120px 80px',
                      gap: '12px',
                      padding: '14px 12px',
                      borderBottom: '1px solid #F1F5F9',
                      alignItems: 'center',
                      transition: 'background 0.1s',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#F8FAFC'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    <div style={{ fontWeight: 600, color: '#1E293B', fontSize: '14px' }}>
                      {m.name}
                    </div>
                    <div style={{ color: '#475569', fontSize: '14px' }}>{m.position}</div>
                    <div>
                      <ShiftBadge shift={m.shift} />
                    </div>
                    <div style={{ color: '#64748B', fontSize: '13px' }}>
                      {m.station != null ? `Station ${m.station}` : '—'}
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        gap: '4px',
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => openEdit(m)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: '#94A3B8',
                          padding: '4px',
                          display: 'flex',
                          borderRadius: '4px',
                        }}
                        title="Edit"
                        onMouseEnter={(e) => { e.currentTarget.style.color = '#3B82F6'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.color = '#94A3B8'; }}
                      >
                        <Pencil size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => confirmDelete(m)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: '#94A3B8',
                          padding: '4px',
                          display: 'flex',
                          borderRadius: '4px',
                        }}
                        title="Remove"
                        onMouseEnter={(e) => { e.currentTarget.style.color = '#EF4444'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.color = '#94A3B8'; }}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* ── FOOTPRINT VIEW ── */}
        {viewMode === 'footprint' && (
          <FootprintView sowData={sowData} crew={crew} />
        )}
      </div>

      {/* ── Add / Edit Modal ── */}
      {showModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            paddingTop: '80px',
            zIndex: 1100,
          }}
          onClick={closeModal}
        >
          <div
            style={{
              background: '#FFFFFF',
              borderRadius: '12px',
              width: '480px',
              maxWidth: '95vw',
              boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
              overflow: 'hidden',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal head */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '20px 24px',
                borderBottom: '1px solid #E5E7EB',
              }}
            >
              <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 600, color: '#111827' }}>
                {editRecord ? 'Edit Crew Member' : 'Add Crew Member'}
              </h3>
              <button
                type="button"
                onClick={closeModal}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#9CA3AF',
                  padding: '4px',
                  display: 'flex',
                }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal body */}
            <div
              style={{
                padding: '24px',
                display: 'flex',
                flexDirection: 'column',
                gap: '18px',
              }}
            >
              {/* Name */}
              <div>
                <label style={labelStyle}>
                  Full Name <span style={{ color: '#EF4444' }}>*</span>
                </label>
                <input
                  type="text"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder="e.g. John Smith"
                  style={inputStyle}
                  onFocus={(e) => { e.target.style.borderColor = '#3B82F6'; }}
                  onBlur={(e) => { e.target.style.borderColor = '#D1D5DB'; }}
                />
              </div>

              {/* Position */}
              <div>
                <label style={labelStyle}>
                  Position / Role <span style={{ color: '#EF4444' }}>*</span>
                </label>
                <SimpleSelect
                  value={formPosition}
                  options={roleOptions}
                  onChange={(v) => {
                    setFormPosition(v);
                    if (!isWelderPosition(v)) setFormStation('');
                  }}
                  placeholder="Select position"
                />
              </div>

              {/* Shift */}
              <div>
                <label style={labelStyle}>
                  Allocated Shift <span style={{ color: '#EF4444' }}>*</span>
                </label>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  {SHIFTS.map((s) => {
                    const Icon = s.icon;
                    const active = formShift === s.value;
                    return (
                      <label
                        key={s.value}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '9px 16px',
                          border: `2px solid ${active ? s.color : '#E5E7EB'}`,
                          borderRadius: '8px',
                          cursor: 'pointer',
                          background: active ? s.bg : '#FAFAFA',
                          fontSize: '13px',
                          fontWeight: active ? 600 : 400,
                          color: active ? s.color : '#374151',
                          transition: 'all 0.15s',
                        }}
                      >
                        <input
                          type="radio"
                          name="shift"
                          value={s.value}
                          checked={active}
                          onChange={() => setFormShift(s.value)}
                          style={{ display: 'none' }}
                        />
                        <Icon size={14} />
                        {s.label}
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Station — only for welders */}
              {isWelderPosition(formPosition) && (
                <div>
                  <label style={labelStyle}>Allocated Station</label>
                  {allStationOptions.length > 0 ? (
                    <SimpleSelect
                      value={formStation}
                      options={[{ value: '', label: '— Not assigned —' }, ...allStationOptions]}
                      onChange={setFormStation}
                      placeholder="Select station"
                    />
                  ) : (
                    <p style={{ margin: 0, fontSize: '13px', color: '#94A3B8', fontStyle: 'italic' }}>
                      No stations defined yet. Set up the Production Line in the Pipelines tab first.
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Modal foot */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '12px',
                padding: '16px 24px',
                background: '#F9FAFB',
                borderTop: '1px solid #E5E7EB',
              }}
            >
              <Button label="Cancel" variant="tertiary" size="medium" onClick={closeModal} />
              <Button
                label={isSaving ? 'Saving…' : editRecord ? 'Update' : 'Add'}
                variant="primary"
                size="medium"
                onClick={handleSave}
                disabled={isSaving || !formName.trim() || !formPosition || !formShift}
              />
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      <ConfirmationModal
        title="Remove Crew Member"
        message={
          recordToDelete ? (
            <>
              Are you sure you want to remove{' '}
              <strong>{recordToDelete.name}</strong> from the manpower setup?
            </>
          ) : (
            'Are you sure you want to remove this crew member?'
          )
        }
        confirmText="Remove"
        cancelText="Cancel"
        isOpen={isDeleteModalOpen}
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          setIsDeleteModalOpen(false);
          setRecordToDelete(null);
        }}
        size="medium"
        confirmButtonProps={{
          style: { backgroundColor: '#EF4444', borderColor: '#EF4444' },
        }}
      />
    </div>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const labelStyle = {
  display: 'block',
  marginBottom: '6px',
  fontSize: '13px',
  fontWeight: 600,
  color: '#374151',
};

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: '8px',
  border: '1px solid #D1D5DB',
  fontSize: '14px',
  outline: 'none',
  color: '#1F2937',
  boxSizing: 'border-box',
  transition: 'border-color 0.15s',
};

// ─── Redux ───────────────────────────────────────────────────────────────────

const mapStateToProps = ({ rolesReduceer, sow }) => ({
  roles: get(rolesReduceer, 'roles', []),
  sowData: get(sow, 'sowData', []),
});

const mapDispatchToProps = {
  onFetchRoles: fetchRoles,
  onFetchSowDetails: fetchSowDetails,
};

export default connect(mapStateToProps, mapDispatchToProps)(DPRSettingsManpowerTab);
